/**
 * @voltxampere/spice — Pulse source codec (V with PULSE)
 *
 * Berkeley SPICE3 syntax:
 *   V{name} N+ N- PULSE(V1 V2 TD TR TF PW PER)
 *
 * Where:
 *   V1  = initial value
 *   V2  = pulsed value
 *   TD  = delay time
 *   TR  = rise time (0 → SPICE treats as instantaneous; we clamp
 *         to 1 ns for Sprint 16 numerical stability)
 *   TF  = fall time (same clamp)
 *   PW  = pulse width
 *   PER = period
 *
 * Schema mapping: `type: "pulse-source"`, params: `{ v0, v1, td,
 * tr, tf, pw, per }`. The 7-tuple maps 1:1.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { parseSpiceNumber, formatSpiceValue } from "../units.js";
import { numericParam, functionName, stripFunctionBody } from "./_param-helpers.js";
const MIN_EDGE_TIME = 1e-9; // 1 ns clamp for instantaneous edges
export const pulseSourceCodec = {
    spiceLetter: "V",
    schemaTypes: ["pulse-source"],
    examples: ["V1 in 0 PULSE(0 5 1m 1u 1u 5m 10m)"],
    parse(card, ctx) {
        if (card.tokens.length < 3) {
            return err("Pulse-V needs 2 nodes and a PULSE(...) clause");
        }
        const [na, nb, ...rest] = card.tokens;
        if (na === undefined || nb === undefined) {
            return err("Pulse-V missing nodes");
        }
        const body = rest.find((tok) => functionName(tok) === "PULSE");
        if (body === undefined) {
            return err("Pulse-V missing PULSE(...) clause");
        }
        const inner = stripFunctionBody(body);
        if (inner === null)
            return err("PULSE body shape mismatch");
        const parts = inner.split(/\s+/).filter((p) => p.length > 0);
        if (parts.length < 7) {
            return err(`PULSE expects 7 parameters, got ${String(parts.length)}`);
        }
        const v0 = parseSpiceNumber(parts[0] ?? "");
        const v1 = parseSpiceNumber(parts[1] ?? "");
        const td = parseSpiceNumber(parts[2] ?? "");
        const trRaw = parseSpiceNumber(parts[3] ?? "");
        const tfRaw = parseSpiceNumber(parts[4] ?? "");
        const pw = parseSpiceNumber(parts[5] ?? "");
        const per = parseSpiceNumber(parts[6] ?? "");
        if (v0 === null ||
            v1 === null ||
            td === null ||
            trRaw === null ||
            tfRaw === null ||
            pw === null ||
            per === null) {
            return err("PULSE param failed to parse");
        }
        let tr = trRaw;
        let tf = tfRaw;
        if (tr <= 0) {
            ctx.warnings.push(`${card.identifier}: PULSE TR=0 clamped to ${String(MIN_EDGE_TIME)}s`);
            tr = MIN_EDGE_TIME;
        }
        if (tf <= 0) {
            ctx.warnings.push(`${card.identifier}: PULSE TF=0 clamped to ${String(MIN_EDGE_TIME)}s`);
            tf = MIN_EDGE_TIME;
        }
        const params = { v0, v1, td, tr, tf, pw, per };
        const component = {
            id: card.identifier,
            type: "pulse-source",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        return ok(component, [na, nb]);
    },
    emit(component, terminals) {
        const v0 = numericParam(component.params.v0, 0);
        const v1 = numericParam(component.params.v1, 5);
        const td = numericParam(component.params.td, 0);
        const tr = numericParam(component.params.tr, MIN_EDGE_TIME);
        const tf = numericParam(component.params.tf, MIN_EDGE_TIME);
        const pw = numericParam(component.params.pw, 1e-3);
        const per = numericParam(component.params.per, 2e-3);
        const [a, b] = terminals;
        const body = [v0, v1, td, tr, tf, pw, per].map(formatSpiceValue).join(" ");
        return `${component.id} ${a ?? "0"} ${b ?? "0"} PULSE(${body})`;
    },
    owns(schemaType) {
        return schemaType === "pulse-source";
    },
};
//# sourceMappingURL=pulse-source.js.map